type TrackingOrder = {
    serviceCategory?: string;
    checkoutMode?: string;
    status?: string;
    paymentStatus?: string;
};

const steps = (entries: string[][]) => entries.map(([key, label]) => ({ key, label }));

const DELIVERY_STEPS = steps([
    ["pending", "Order Placed"],
    ["paid", "Payment Confirmed"],
    ["assigned", "Driver Assigned"],
    ["awaiting_pickup", "Awaiting Pickup"],
    ["arrived_at_pickup", "Arrived at Pickup"],
    ["picked_up", "Picked Up"],
    ["in_transit", "In Transit"],
    ["out_for_delivery", "Out for Delivery"],
    ["arrived_at_delivery", "Arrived at Delivery"],
    ["proof_of_delivery_pending", "Awaiting Delivery Confirmation"],
    ["delivered", "Delivered"],
    ["completed", "Completed"],
]);

const SERVICE_STEPS: Record<string, ReturnType<typeof steps>> = {
    accommodation: steps([
        ["pending", "Booking Placed"], ["paid", "Payment Confirmed"],
        ["confirmed", "Booking Confirmed"], ["ready_for_check_in", "Ready for Check-in"],
        ["checked_in", "Checked In"], ["in_stay", "In Stay"],
        ["checked_out", "Checked Out"], ["completed", "Completed"],
    ]),
    car_hire: steps([
        ["pending", "Booking Placed"], ["paid", "Payment Confirmed"],
        ["confirmed", "Booking Confirmed"], ["ready_for_pickup", "Ready for Collection"],
        ["vehicle_collected", "Vehicle Collected"], ["in_progress", "In Progress"],
        ["return_due", "Return Due"], ["vehicle_returned", "Vehicle Returned"],
        ["inspection_pending", "Inspection Pending"], ["completed", "Completed"],
    ]),
    trip: steps([
        ["pending", "Booking Placed"], ["paid", "Payment Confirmed"],
        ["confirmed", "Booking Confirmed"], ["checked_in", "Checked In"],
        ["onboarded", "Onboarded"], ["in_progress", "In Progress"],
        ["completed", "Completed"],
    ]),
};

const COLLECTION_STEPS = steps([
    ["pending", "Order Placed"], ["paid", "Payment Confirmed"],
    ["confirmed", "Order Confirmed"], ["processing", "Preparing"],
    ["ready_for_collection", "Ready for Collection"], ["completed", "Completed"],
]);

export function getOrderTracking(order: TrackingOrder) {
    const serviceSteps = SERVICE_STEPS[order.serviceCategory || ""];
    const isDelivery = !serviceSteps && (
        order.serviceCategory === "delivery" || order.checkoutMode === "delivery"
    );
    let timeline = isDelivery ? DELIVERY_STEPS : serviceSteps || COLLECTION_STEPS;
    if (isDelivery && order.serviceCategory === "product") {
        timeline = [
            ...DELIVERY_STEPS.slice(0, 2),
            ...steps([["confirmed", "Order Confirmed"], ["processing", "Preparing"],
                ["ready_for_collection", "Ready for Dispatch"]]),
            ...DELIVERY_STEPS.slice(2, 6),
            ...steps([["shipped", "Shipped"]]),
            ...DELIVERY_STEPS.slice(6),
        ];
    }
    let status = String(order.status || "pending").trim().toLowerCase().replace(/[\s-]+/g, "_");
    const paymentStatus = String(order.paymentStatus || "pending").trim().toLowerCase();
    const paymentReversed = ["refunded", "partially_refunded", "reversed", "chargeback"].includes(paymentStatus);
    const isPaid = !paymentReversed && (["paid", "cash_collected"].includes(paymentStatus) || ["paid", "cash_collected"].includes(status));
    const aliases: Record<string, string> = {
        created: "pending", placed: "pending", canceled: "cancelled",
        complete: "completed", done: "completed", booked: "confirmed",
        accepted: "confirmed", approved: "confirmed", cash_collected: "paid",
    };
    const categoryAliases: Record<string, Record<string, string>> = {
        accommodation: {
            ready: "ready_for_check_in", check_in_ready: "ready_for_check_in",
            checkin: "checked_in", checkedin: "checked_in", staying: "in_stay",
            checkout: "checked_out", checkedout: "checked_out",
        },
        car_hire: {
            ready: "ready_for_pickup", collected: "vehicle_collected", picked_up: "vehicle_collected",
            active: "in_progress", due_for_return: "return_due", returned: "vehicle_returned", inspected: "completed",
        },
        trip: {
            checkin: "checked_in", checkedin: "checked_in", boarded: "onboarded",
            onboard: "onboarded", departed: "in_progress", active: "in_progress",
        },
        product: {
            preparing: "processing", ready: "ready_for_collection", ready_for_pickup: "ready_for_collection",
            dispatched: "shipped", returning: "return_in_transit", returned_to_sender: "returned",
        },
        delivery: { shipped: "in_transit", returned: "returned_to_sender" },
    };
    status = categoryAliases[order.serviceCategory || "product"]?.[status] || aliases[status] || status;
    if (status === "paid" && paymentReversed) status = paymentStatus;
    if (status === "pending" && isPaid) status = "paid";

    // Invalid legacy dispatch states on a service booking must not invent progress.
    if (!isDelivery && /transit|deliver|shipped|assigned|awaiting_pickup|arrived_at_pickup/.test(status)) {
        status = "status_unavailable";
    }

    const label = status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    if (["cancelled", "no_show", "returned", "returned_to_sender"].includes(status)) {
        timeline = steps([[status, label]]);
    } else if (!timeline.some((step) => step.key === status)) {
        // An unfamiliar status must not mark later milestones as completed.
        timeline = steps([[status, label]]);
    }

    return { steps: timeline, status, label: timeline.find((step) => step.key === status)!.label, isDelivery, isPaid, paymentStatus };
}
